import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage, API_BASE } from "@/lib/api";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function CalendarPage() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedPath, setFeedPath] = useState("");

  useEffect(() => {
    api
      .get("/drivers/me/calendar-feed")
      .then((r) => setFeedPath(r.data.feed_path))
      .catch(() => {}); // feed button just stays disabled
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/drivers/me/calendar`, { params: { month } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [month]);

  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const daysInMonth = new Date(y, m, 0).getDate();
  const startDow = new Date(y, m - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${month}-${String(d).padStart(2, "0")}`);
  while (cells.length % 7) cells.push(null);

  const blackouts = data?.blackouts || [];
  const jobs = data?.jobs || [];
  const blackoutOn = (day) => blackouts.some((r) => r.start <= day && day <= r.end);
  const jobsOn = (day) => jobs.filter((j) => j.start <= day && day <= j.end);

  const shift = (delta) => {
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const today = new Date().toISOString().slice(0, 10);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="px-6 md:px-10 py-10 md:py-14 max-w-6xl mx-auto" data-testid="calendar-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#FF3B30] font-bold">/ Schedule</span>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase mt-2 leading-none">
            Haul calendar
          </h1>
          <p className="text-sm text-zinc-500 mt-3">
            Accepted jobs against your blackout dates. Manage blackouts in Profile.
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <a
              href={feedPath ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(`${API_BASE}${feedPath}`)}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="add-to-google-calendar"
              aria-disabled={!feedPath}
              className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border transition-colors ${
                feedPath
                  ? "border-[#FF3B30] bg-[#FF3B30] text-white hover:bg-[#ff5044]"
                  : "border-white/10 text-zinc-600 pointer-events-none"
              }`}
            >
              <CalendarPlus className="w-3.5 h-3.5" /> Add to Google Calendar
            </a>
            <a
              href={feedPath ? `${API_BASE}${feedPath}`.replace(/^https?:\/\//, "webcal://") : undefined}
              data-testid="add-to-apple-calendar"
              aria-disabled={!feedPath}
              className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 border transition-colors ${
                feedPath
                  ? "border-white/20 text-zinc-200 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  : "border-white/10 text-zinc-600 pointer-events-none"
              }`}
            >
              <CalendarPlus className="w-3.5 h-3.5" /> Apple Calendar
            </a>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">
            Subscribes your haul feed — new jobs and blackout dates appear automatically. Outlook: copy the feed link in Profile and use "Subscribe from web".
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" aria-label="Previous month"
            className="border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-white p-2.5 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display text-xl font-black uppercase w-44 text-center" data-testid="cal-month-label">
            {monthLabel}
          </div>
          <button onClick={() => shift(1)} data-testid="cal-next" aria-label="Next month"
            className="border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-white p-2.5 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-500/40 border border-red-500/60" /> Blackout</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-500/40 border border-amber-500/60" /> Assigned</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-500/40 border border-emerald-500/60" /> Completed</span>
      </div>

      <div className={`grid grid-cols-7 gap-px bg-white/[0.06] border border-white/[0.06] ${loading ? "opacity-50" : ""}`} data-testid="calendar-grid">
        {DOW.map((d) => (
          <div key={d} className="bg-[#0A0A0A] text-center text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-bold py-2">
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`x${i}`} className="bg-[#0A0A0A] min-h-24 md:min-h-28" />;
          const bo = blackoutOn(day);
          const dayJobs = jobsOn(day);
          return (
            <div
              key={day}
              data-testid={`cal-day-${day}`}
              className={`min-h-24 md:min-h-28 p-1.5 md:p-2 bg-[#0F0F10] ${
                bo ? "bg-red-500/[0.08]" : ""
              } ${day === today ? "outline outline-1 outline-[#FF3B30] -outline-offset-1" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-bold ${day === today ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                  {Number(day.slice(8))}
                </span>
                {bo && <span className="text-[8px] uppercase tracking-widest font-black text-red-400">Blackout</span>}
              </div>
              <div className="mt-1 space-y-1">
                {dayJobs.map((j) => (
                  <Link
                    key={j.request_id}
                    to={`/app/requests/${j.request_id}`}
                    data-testid={`cal-job-${j.request_id}`}
                    className={`block text-[9px] md:text-[10px] uppercase tracking-wide font-bold px-1.5 py-1 truncate border transition-colors ${
                      j.status === "completed"
                        ? "border-emerald-500/50 bg-emerald-500/[0.08] text-emerald-300 hover:bg-emerald-500/20"
                        : "border-amber-500/50 bg-amber-500/[0.08] text-amber-300 hover:bg-amber-500/20"
                    }`}
                    title={`${j.title} · $${j.amount_usd.toLocaleString()}`}
                  >
                    {j.title}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {!loading && jobs.length > 0 && (
        <div className="mt-8 space-y-2" data-testid="calendar-job-list">
          {jobs.map((j) => (
            <Link
              key={j.request_id}
              to={`/app/requests/${j.request_id}`}
              className="flex items-center justify-between border border-white/[0.08] bg-[#0F0F10] p-4 hover:border-[#FF3B30] transition-colors"
            >
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">{j.title}</div>
                <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                  <MapPin className="w-3 h-3 text-[#FF3B30]" />
                  {j.pickup_city} → {j.delivery_city}
                  <span className="text-zinc-700">·</span>
                  <span className="uppercase tracking-widest text-[10px] font-bold">
                    {j.start === j.end ? j.start : `${j.start} → ${j.end}`}
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0 ml-4">
                <div className="font-display text-lg font-black">${j.amount_usd.toLocaleString()}</div>
                <div className={`text-[10px] uppercase tracking-widest font-black ${j.status === "completed" ? "text-emerald-400" : "text-amber-400"}`}>
                  {j.status}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
